// src/components/games/crossroads/CrossroadsMultiplayerHub.tsx
// Main hub for Crossroads TCG multiplayer - handles lobby, invites, and active games

import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonIcon,
} from '@ionic/react';
import { arrowBack } from 'ionicons/icons';
import { useLocation } from 'react-router-dom';
import GameLobby from './GameLobby';
import { ChallengePlayerModal } from './ChallengePlayerModal';
import GameInviteAcceptModal from './GameInviteAcceptModal';
import MultiplayerCrossroads from './MultiplayerCrossroads';
import './CrossroadsMultiplayerHub.css';

interface CrossroadsMultiplayerHubProps {
  onBack: () => void;
}

enum ViewState {
  LOBBY = 'lobby',
  ACTIVE_GAME = 'active_game',
}

export const CrossroadsMultiplayerHub: React.FC<CrossroadsMultiplayerHubProps> = ({ onBack }) => {
  const location = useLocation<any>();
  const [viewState, setViewState] = useState<ViewState>(ViewState.LOBBY);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [showInviteAcceptModal, setShowInviteAcceptModal] = useState(false);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [activeGameId, setActiveGameId] = useState<string | null>(null);
  const [playerIndex, setPlayerIndex] = useState<number>(0);

  // Auto-join game from chat invitation
  useEffect(() => {
    if (location.state?.autoJoinGameId) {
      const gameId = location.state.autoJoinGameId;
      const playerIdx = location.state.playerIndex || 1;
      handleJoinGame(gameId, playerIdx);

      // Clear the state so it doesn't auto-join again on back navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleJoinGame = (gameId: string, playerIdx: number) => {
    setActiveGameId(gameId);
    setPlayerIndex(playerIdx);
    setViewState(ViewState.ACTIVE_GAME);
  };

  const handleGameCreated = (gameId: string, playerIdx: number) => {
    setActiveGameId(gameId);
    setPlayerIndex(playerIdx);
    setViewState(ViewState.ACTIVE_GAME);
    setShowInviteAcceptModal(false);
  };

  const handleExitGame = () => {
    setActiveGameId(null);
    setPlayerIndex(0);
    setViewState(ViewState.LOBBY);
  };

  const handleOpenInviteAccept = (data: any) => {
    setInvitationData(data);
    setShowInviteAcceptModal(true);
  };

  // Render active game
  if (viewState === ViewState.ACTIVE_GAME && activeGameId) {
    return (
      <MultiplayerCrossroads
        gameId={activeGameId}
        playerIndex={playerIndex}
        onExit={handleExitGame}
      />
    );
  }

  // Render lobby
  return (
    <>
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onBack}>
                <IonIcon icon={arrowBack} />
              </IonButton>
            </IonButtons>
            <IonTitle>Crossroads TCG Multiplayer</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <GameLobby
            onJoinGame={handleJoinGame}
            onChallengePlayer={() => setShowChallengeModal(true)}
          />
        </IonContent>
      </IonPage>

      {/* Challenge Modal */}
      <ChallengePlayerModal
        isOpen={showChallengeModal}
        onDismiss={() => setShowChallengeModal(false)}
      />

      {/* Invite Accept Modal */}
      <GameInviteAcceptModal
        isOpen={showInviteAcceptModal}
        onDismiss={() => setShowInviteAcceptModal(false)}
        invitationData={invitationData}
        onGameCreated={handleGameCreated}
      />
    </>
  );
};

export default CrossroadsMultiplayerHub;
